import * as extensions from "@afriannotate/extensions";
import { useEffect, useRef, useState, useCallback } from "react";
import { useHistory } from "react-router";
import { Button, Badge } from "@humansignal/ui";
import { IconWarningCircleFilled, IconCode, IconBook, IconExternal, IconCopyOutline } from "@humansignal/icons";
import { Form, Input } from "../../components/Form";
import { Modal } from "../../components/Modal/Modal";
import { Space } from "../../components/Space/Space";
import { useAPI } from "../../providers/ApiProvider";
import { useUpdatePageTitle, createTitleFromSegments } from "@humansignal/core";
import { useProject } from "../../providers/ProjectProvider";
import { useFixedLocation, useParams } from "../../providers/RoutesProvider";
import { cn } from "../../utils/bem";
import { isDefined, copyText } from "../../utils/helpers";
import { useTranslation } from "../../i18n/LocaleProvider";
import { downloadFile as downloadUrl } from "../../utils/download";
import { apiUrl } from "../../utils/jwt-storage";
import "./ExportPage.prefix.css";

// Community Edition exports run synchronously in a single HTTP request.
// Large exports can exceed typical proxy timeouts, so we warn early and link to alternatives.
const LARGE_EXPORT_TASK_THRESHOLD = 1000;
const EXPORT_TIMEOUT_DOCS_URL = "https://afriannotate.waraka.org/docs/data-import/export#large-exports-and-timeouts";
const EXPORT_SNAPSHOT_SDK_URL = "https://afriannotate.waraka.org/docs/api/sdk";
// AfriAnnotate community Discord (server: Waraka) — where we point people
// for help instead of an upstream Slack/forum.
const DISCORD_HELP_URL = "https://discord.gg/dUV4xu6ZE";

// const formats = {
//   json: 'JSON',
//   csv: 'CSV',
// };

const downloadFile = (blob, filename) => {
  const link = document.createElement("a");

  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
};

const wait = () => new Promise((resolve) => setTimeout(resolve, 5000));

const isTimeoutLikeStatus = (status) => status === 408 || status === 502 || status === 504;

export const ExportPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useFixedLocation();
  const pageParams = useParams();
  const api = useAPI();
  const { project } = useProject();
  useUpdatePageTitle(createTitleFromSegments([project?.title, "Export"]));

  const [previousExports, setPreviousExports] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [downloadingMessage, setDownloadingMessage] = useState(false);
  const [availableFormats, setAvailableFormats] = useState([]);
  const [currentFormat, setCurrentFormat] = useState("JSON");
  const [projectTaskNumber, setProjectTaskNumber] = useState(null);
  const [exportIssue, setExportIssue] = useState(null);
  const [downloadingBundle, setDownloadingBundle] = useState(false);
  // Export shape is chosen on the full-page Preview; the dialog exports
  // the default shape (all annotations, complete fields). The preview
  // page has its own Export button for shaped downloads.
  const shape = { annotationChoice: "all", fields: null };
  const [previewMeta, setPreviewMeta] = useState(null);
  const openPreviewPage = () => {
    const q = new URLSearchParams(location.search || "");
    q.set("format", currentFormat);
    history.push(`/projects/${pageParams.id}/export-preview?${q.toString()}`);
  };
  const viewId = (() => {
    try {
      return new URLSearchParams(location.search || "").get("view");
    } catch (_e) {
      return null;
    }
  })();

  // Projects that collect in-app recordings get a one-click
  // "annotations + media" ZIP — no scripts, no URL fetching.
  const hasRecorders = /<\s*(VideoRecord|AudioRecord|ImageRecord)\b/i.test(project?.label_config ?? "");
  const proceedMediaBundle = async () => {
    setExportIssue(null);
    setDownloadingBundle(true);
    try {
      const response = await api.callApi("exportMediaBundleRaw", {
        params: { pk: pageParams.id },
      });
      if (!response) {
        setExportIssue("timeout");
        return;
      }
      if (response.ok) {
        const blob = await response.blob();
        downloadFile(blob, response.headers.get("filename") || `project-${pageParams.id}-with-media.zip`);
        return;
      }
      if (isTimeoutLikeStatus(response.status)) {
        setExportIssue("timeout");
        return;
      }
      api.handleError(response);
    } finally {
      setDownloadingBundle(false);
    }
  };

  /** @type {import('react').RefObject<Form>} */
  const form = useRef();

  const proceedExport = async () => {
    setExportIssue(null);
    setDownloading(true);

    const messageTimer = window.setTimeout(() => {
      setDownloadingMessage(true);
    }, 1000);

    try {
      const params = form.current.assembleFormData({
        asJSON: true,
        full: true,
        booleansAsNumbers: true,
      });

      const shapeParams = {};
      if (viewId) shapeParams.view = viewId;
      if (shape.annotationChoice && shape.annotationChoice !== "all")
        shapeParams.annotation_choice = shape.annotationChoice;
      if (shape.fields && shape.fields.length) shapeParams.fields = shape.fields.join(",");

      const response = await api.callApi("exportRaw", {
        params: {
          pk: pageParams.id,
          ...params,
          ...shapeParams,
        },
      });

      // The API proxy can return `null` for certain network errors; treat it as timeout-like
      // and show actionable guidance instead of a generic error.
      if (!response) {
        setExportIssue("timeout");
        return;
      }

      if (response.ok) {
        const blob = await response.blob();

        downloadFile(blob, response.headers.get("filename"));
        return;
      }

      if (isTimeoutLikeStatus(response.status)) {
        setExportIssue("timeout");
        return;
      }

      api.handleError(response);
    } finally {
      window.clearTimeout(messageTimer);
      setDownloading(false);
      setDownloadingMessage(false);
    }
  };

  useEffect(() => {
    if (isDefined(pageParams.id)) {
      let cancelled = false;

      api
        .callApi("previousExports", {
          params: {
            pk: pageParams.id,
          },
        })
        .then(({ export_files }) => {
          if (!cancelled) setPreviousExports(export_files.slice(0, 1));
        });

      api
        .callApi("exportFormats", {
          params: {
            pk: pageParams.id,
          },
        })
        .then((formats) => {
          if (cancelled) return;
          setAvailableFormats(formats);
          setCurrentFormat(formats[0]?.name);
        });

      // Scope count for the header/summary even before Preview is opened.
      api
        .callApi("exportPreview", {
          params: { pk: pageParams.id, limit: 1, ...(viewId ? { view: viewId } : {}) },
          errorFilter: () => true,
        })
        .then((res) => {
          if (cancelled || !res || res.count == null) return;
          setPreviewMeta((m) => m ?? { count: res.count, skipped: res.skipped ?? 0 });
        });

      // Fetch project metadata to show a proactive warning for large exports.
      // This is best-effort and should not trigger global error UI if it fails.
      api
        .callApi("project", {
          params: { pk: pageParams.id },
          errorFilter: () => true,
        })
        .then((project) => {
          if (cancelled) return;
          setProjectTaskNumber(project?.task_number ?? null);
        });

      return () => {
        cancelled = true;
      };
    }
  }, [pageParams.id]);

  return (
    <Modal
      onHide={() => {
        const path = location.pathname.replace(ExportPage.path, "");
        const search = location.search;

        history.replace(`${path}${search !== "?" ? search : ""}`);
      }}
      title={t("pages.exportpage.export_page.export_data")}
      header={
        <div className={cn("export-page").elem("header-actions").toClassName()}>
          <Button look="outlined" onClick={openPreviewPage} aria-label="Open the export preview">
            Preview
          </Button>
          <Button
            onClick={proceedExport}
            waiting={downloading}
            aria-label="Export data"
            className={cn("export-page").elem("export-button").toClassName()}
          >
            <span className={cn("export-page").elem("label-full").toClassName()}>
              {previewMeta?.count != null
                ? `Export ${Math.max(0, previewMeta.count - (previewMeta.skipped || 0)).toLocaleString()} tasks as ${currentFormat}`
                : `Export as ${currentFormat}`}
            </span>
            <span className={cn("export-page").elem("label-short").toClassName()}>Export {currentFormat}</span>
          </Button>
        </div>
      }
      style={{ width: "min(760px, calc(100vw - 24px))" }}
      closeOnClickOutside={false}
      allowClose={!downloading}
      // footer="Read more about supported export formats in the Documentation."
      visible
    >
      <div className={cn("export-page").toClassName()}>
        {/* Always-visible summary of what the Export button will do. */}
        <div className={cn("export-page").elem("summary").toClassName()}>
          <span className={cn("export-page").elem("chip").mod({ primary: true }).toClassName()}>{currentFormat}</span>
          <span className={cn("export-page").elem("chip").toClassName()}>
            {viewId ? "Current Data Manager view" : "Annotated tasks"}
            {previewMeta?.count != null ? `: ${previewMeta.count.toLocaleString()}` : ""}
            {previewMeta?.skipped ? ` (−${previewMeta.skipped.toLocaleString()} without accepted)` : ""}
          </span>
          <span className={cn("export-page").elem("chip").toClassName()}>
            Annotations:{" "}
            {{ all: "all", first: "first", latest: "latest", accepted: "accepted only" }[shape.annotationChoice] ??
              shape.annotationChoice}
          </span>
          <span className={cn("export-page").elem("chip").toClassName()}>
            Fields: {shape.fields ? `${shape.fields.length} selected` : "default (no platform bookkeeping)"}
          </span>
          <button
            type="button"
            className={cn("export-page").elem("summary-link").toClassName()}
            onClick={openPreviewPage}
          >
            Shape &amp; preview →
          </button>
        </div>

        <FormatInfo
          availableFormats={availableFormats}
          selected={currentFormat}
          onClick={(format) => setCurrentFormat(format.name)}
        />

        {hasRecorders && (
          <div className={cn("export-page").elem("bundle").toClassName()}>
            <div className={cn("export-page").elem("bundle-text").toClassName()}>
              <div className={cn("export-page").elem("bundle-title").toClassName()}>Recordings included</div>
              <div className={cn("export-page").elem("bundle-body").toClassName()}>
                This project collects in-app recordings. Download one ZIP with the annotations plus every recorded clip
                (and skeleton file) — the JSON links point at the bundled files, nothing else to fetch.
              </div>
            </div>
            <Button
              look="outlined"
              onClick={proceedMediaBundle}
              waiting={downloadingBundle}
              aria-label="Download annotations with media"
            >
              Download ZIP with media
            </Button>
          </div>
        )}

        {/* Cards an installed extension adds (each decides whether it applies). */}
        {(extensions.exportCards ?? []).map((card) => (
          <card.component key={card.id} project={project} />
        ))}

        <ExportLargeProjectWarning taskCount={projectTaskNumber} />
        {exportIssue === "timeout" && <ExportTimeoutGuidance projectId={pageParams.id} exportType={currentFormat} />}

        <Form ref={form}>
          <Input type="hidden" name="exportType" value={currentFormat} />
        </Form>

        <div className={cn("export-page").elem("footer").toClassName()}>
          {downloadingMessage && (
            <div className={cn("export-page").elem("status-message").toClassName()}>
              Files are being prepared. It might take long time.
            </div>
          )}
          <Space style={{ width: "100%" }} spread>
            <div className={cn("export-page").elem("recent").toClassName()}>
              <a className="no-go" href={EXPORT_TIMEOUT_DOCS_URL} target="_blank" rel="noreferrer">
                Having a timeout or trouble exporting large projects?
              </a>
            </div>
            <div className={cn("export-page").elem("actions").toClassName()} />
          </Space>
        </div>
      </div>
    </Modal>
  );
};

const FormatInfo = ({ availableFormats, selected, onClick }) => {
  return (
    <div className={cn("formats").toClassName()}>
      <div className={cn("formats").elem("info").toClassName()}>
        You can export dataset in one of the following formats:
      </div>
      <div className={cn("formats").elem("list").toClassName()}>
        {availableFormats.map((format) => (
          <div
            key={format.name}
            className={cn("formats")
              .elem("item")
              .mod({
                active: !format.disabled,
                selected: format.name === selected,
              })
              .toClassName()}
            onClick={!format.disabled ? () => onClick(format) : null}
          >
            <div className={cn("formats").elem("name").toClassName()}>
              {format.title}

              <Space size="small">
                {format.tags?.map?.((tag, index) => {
                  // Map tag text to badge variant
                  const tagLower = tag?.toLowerCase() || "";
                  let variant = "primary";
                  if (tagLower === "enterprise" || tagLower.includes("enterprise")) {
                    variant = "gradient";
                  } else if (tagLower === "beta") {
                    variant = "plum";
                  } else if (tagLower === "new" || tagLower.includes("new")) {
                    variant = "positive";
                  }

                  return (
                    <Badge key={index} variant={variant} size="small">
                      {tag}
                    </Badge>
                  );
                })}
              </Space>
            </div>

            {format.description && (
              <div className={cn("formats").elem("description").toClassName()}>{format.description}</div>
            )}
          </div>
        ))}
      </div>
      <div className={cn("formats").elem("feedback").toClassName()}>
        Can't find an export format?
        <br />
        Please let us know in{" "}
        <a className="no-go" href={DISCORD_HELP_URL} target="_blank" rel="noreferrer">
          our Discord
        </a>{" "}
        or submit an issue to the{" "}
        <a
          className="no-go"
          href="https://github.com/warakacommunity/afriannotate/issues"
          target="_blank"
          rel="noreferrer"
        >
          repository
        </a>
      </div>
    </div>
  );
};

ExportPage.path = "/export";
ExportPage.modal = true;

const ExportLargeProjectWarning = ({ taskCount }) => {
  if (!Number.isFinite(taskCount) || taskCount < LARGE_EXPORT_TASK_THRESHOLD) return null;

  return (
    <div className={cn("export-page").elem("warning").toClassName()}>
      <div className={cn("export-page").elem("warning-title").toClassName()}>
        Large project detected ({taskCount.toLocaleString()} tasks)
      </div>
      <div className={cn("export-page").elem("warning-body").toClassName()}>
        Large exports can exceed the request timeout. For a reliable export, start an asynchronous snapshot via the{" "}
        <a className="no-go" href={EXPORT_SNAPSHOT_SDK_URL} target="_blank" rel="noreferrer">
          API / SDK
        </a>{" "}
        , or export in smaller batches from the Data Manager.
      </div>
    </div>
  );
};

const ExportTimeoutGuidance = ({ projectId }) => {
  const [copied, setCopied] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "https://your-host";
  // The reliable path: kick off an asynchronous export snapshot. It's built
  // in the background, so there's no single-request timeout — poll, then
  // download. This is AfriAnnotate's actual solution (no CLI, no Enterprise).
  const apiCommand = `curl -X POST "${origin}/api/projects/${projectId}/exports/" \\\n  -H "Authorization: Token <YOUR_TOKEN>"`;

  const handleCopy = useCallback(() => {
    copyText(apiCommand);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [apiCommand]);

  return (
    <div className={cn("export-page").elem("timeout").toClassName()}>
      <div className={cn("export-page").elem("timeout-header").toClassName()}>
        <IconWarningCircleFilled className={cn("export-page").elem("timeout-icon").toClassName()} />
        <div className={cn("export-page").elem("timeout-title").toClassName()}>Export timed out</div>
      </div>
      <div className={cn("export-page").elem("timeout-body").toClassName()}>
        In-browser export runs in a single request, so very large datasets can exceed the reverse-proxy timeout (often
        ~90&nbsp;seconds). Here are two reliable ways to get your data out.
      </div>

      <div className={cn("export-page").elem("timeout-actions").toClassName()}>
        <ul className={cn("export-page").elem("timeout-actions-list").toClassName()}>
          <li>
            <div className={cn("export-page").elem("timeout-action-item").toClassName()}>
              <IconCode className={cn("export-page").elem("timeout-action-icon").toClassName()} />
              <div className={cn("export-page").elem("timeout-action-content").toClassName()}>
                <span className={cn("export-page").elem("timeout-action-label").toClassName()}>
                  Export via the API — recommended
                </span>
                <span>
                  Start an asynchronous snapshot; it&rsquo;s prepared in the background with no request timeout, then
                  poll and download it. See the{" "}
                  <a className="no-go" href={EXPORT_SNAPSHOT_SDK_URL} target="_blank" rel="noreferrer">
                    API &amp; SDK guide
                    <IconExternal className={cn("export-page").elem("timeout-link-icon").toClassName()} />
                  </a>{" "}
                  for the full flow.
                </span>
                <div className={cn("export-page").elem("timeout-code-wrapper").toClassName()}>
                  <pre className={cn("export-page").elem("timeout-code").toClassName()}>
                    <code>{apiCommand}</code>
                  </pre>
                  <button
                    type="button"
                    className={cn("export-page").elem("timeout-copy-button").toClassName()}
                    onClick={handleCopy}
                    aria-label="Copy command"
                    title={copied ? "Copied!" : "Copy command"}
                  >
                    <IconCopyOutline className={cn("export-page").elem("timeout-copy-icon").toClassName()} />
                    {copied && (
                      <span className={cn("export-page").elem("timeout-copy-text").toClassName()}>Copied!</span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </li>
          <li>
            <div className={cn("export-page").elem("timeout-action-item").toClassName()}>
              <IconCode className={cn("export-page").elem("timeout-action-icon").toClassName()} />
              <div className={cn("export-page").elem("timeout-action-content").toClassName()}>
                <span className={cn("export-page").elem("timeout-action-label").toClassName()}>
                  Export in smaller batches
                </span>
                <span>
                  In the Data Manager, filter to a subset (by date, status, or a saved tab) and export each slice — each
                  request stays well under the timeout.
                </span>
              </div>
            </div>
          </li>
        </ul>

        <div className={cn("export-page").elem("timeout-footer").toClassName()}>
          <span className={cn("export-page").elem("timeout-footer-links").toClassName()}>
            <a className="no-go" href={EXPORT_TIMEOUT_DOCS_URL} target="_blank" rel="noreferrer">
              <IconBook className={cn("export-page").elem("timeout-footer-icon").toClassName()} />
              Full guide
            </a>
            <a className="no-go" href={DISCORD_HELP_URL} target="_blank" rel="noreferrer">
              Stuck? Ask in our Discord
              <IconExternal className={cn("export-page").elem("timeout-link-icon").toClassName()} />
            </a>
          </span>
        </div>
      </div>
    </div>
  );
};
